import { z } from 'zod';
import { type CountryCode, countryCodeSchema } from './countries';
import { orgRoleSchema } from './permissions';

const timestamp = z.iso.datetime({ offset: true });

export const currencySchema = z.enum(['USD', 'CAD']);
export type Currency = z.infer<typeof currencySchema>;

/** Billing currency of a new org (Q15): CAD for Canada, USD everywhere else. Mirrors create_organization_for. */
export function currencyForCountry(country: CountryCode | string): Currency {
  return country === 'CA' ? 'CAD' : 'USD';
}

/** An org or workspace name: trimmed, 1–80 characters. */
export const entityNameSchema = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(80, 'Max 80 characters');

/** A row of public.organizations. */
export const organizationSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  country: z.string().regex(/^[A-Z]{2}$/),
  currency: currencySchema,
  created_by: z.uuid().nullable(),
  deleted_at: timestamp.nullable(),
  created_at: timestamp,
});
export type Organization = z.infer<typeof organizationSchema>;

/** A row of public.org_memberships. */
export const orgMembershipSchema = z.object({
  organization_id: z.uuid(),
  user_id: z.uuid(),
  scope: z.literal('org'),
  role: orgRoleSchema,
  created_at: timestamp,
});
export type OrgMembership = z.infer<typeof orgMembershipSchema>;

/** Input for create_organization. */
export const createOrganizationInputSchema = z.object({
  name: entityNameSchema,
  country: countryCodeSchema,
});
export type CreateOrganizationInput = z.infer<typeof createOrganizationInputSchema>;

/** Error codes raised by the identity RPCs (the PostgREST error `message`; 42501 arrives as `forbidden`). */
export const IDENTITY_ERROR_CODES = [
  'forbidden',
  'invalid_name',
  'invalid_country',
  'invalid_role',
  'not_a_member',
  'owner_only',
  'last_owner',
  'workspace_archived',
  'cross_org',
  // invitations
  'invalid_email',
  'invalid_grants',
  'already_a_member',
  'invitation_not_found',
  'invitation_expired',
  'invitation_revoked',
  'invitation_used',
  'invitation_not_pending',
  'invitation_email_mismatch',
  'email_not_confirmed',
  // staff
  'invalid_currency',
  'currency_locked',
  'user_not_found',
  'last_staff_admin',
  // script review
  'version_not_found',
  'version_not_processed',
  'previous_not_confirmed',
  // pricing
  'invalid_matrix',
  'invalid_discount',
  'price_list_name_taken',
  'price_list_not_found',
  'currency_mismatch',
  // billing profile
  'invalid_billing_profile',
  'invalid_billing_emails',
  'invalid_tax_id',
  // orders
  'invalid_order_options',
  'product_mismatch',
  'product_unavailable',
  'project_not_draft',
  'invalid_project_details',
  // script intake
  'script_required',
  'script_already_uploaded',
  'script_unreadable',
  'script_too_long',
  'not_a_pdf',
  'file_not_found',
  'invalid_file',
  'file_too_large',
  'extractor_unavailable',
  'project_details_incomplete',
  'invalid_time_zone',
  'invalid_reference',
  'order_exists',
  'order_not_draft',
  'order_not_found',
  'billing_profile_incomplete',
  'reference_required',
  'open_orders',
  'illegal_transition',
  'order_not_awaiting_payment',
  // stored cards
  'payment_method_not_found',
  // payment on account
  'invalid_payment_mode',
  'invalid_credit_limit',
  'account_mode_required',
  // ledger and invoices
  'invalid_invoice',
  'charge_not_invoiceable',
  'invalid_invoice_discount',
  'invalid_external_ref',
  'invoice_not_found',
  'invoice_not_issued',
  'invoice_void',
  'memo_required',
  'invalid_memo',
  'invalid_amount',
  'entry_not_reversible',
  'balance_outstanding',
  // on-account orders
  'payment_mode_changed',
  'credit_limit_reached',
  'charge_invoiced',
] as const;
export type IdentityErrorCode = (typeof IDENTITY_ERROR_CODES)[number];

/**
 * Slug preview for a new org or workspace name. Mirrors `public.slugify` in the identity migration
 * (apostrophes dropped, anything else non-alphanumeric becomes a hyphen, 48 characters). The database
 * allocates the real slug and may add a numeric suffix.
 */
export function slugify(name: string, fallback: string): string {
  const slug = name
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || fallback;
}
